import React, { useState, useEffect } from 'react';
import { 
  Search, 
  Filter, 
  Clock, 
  DollarSign, 
  MapPin, 
  Calendar, 
  Star, 
  CheckCircle, 
  XCircle, 
  Eye, 
  Send, 
  FileText, 
  AlertTriangle, 
  TrendingUp,
  Users,
  Award,
  Shield,
  ArrowRight,
  Plus,
  MessageSquare,
  Download
} from 'lucide-react';

const VerificationRequestManager = ({ userType = 'project-owner', userId = '1' }) => {
  const [activeTab, setActiveTab] = useState('active-requests');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showProposalModal, setShowProposalModal] = useState(false);
  const [proposalData, setProposalData] = useState({
    price: '',
    timeline: '',
    methodology: '',
    scope: '',
    deliverables: '',
    experience: '',
    references: ''
  });

  // Mock data for verification requests
  const [verificationRequests] = useState([
    {
      id: 'VR001',
      projectId: 'P001',
      projectName: 'Amazon Rainforest Restoration',
      projectType: 'Reforestation',
      location: 'Brazil',
      status: 'pending',
      requestDate: '2024-01-15',
      deadline: '2024-04-15',
      budgetRange: '50000-100000',
      preferredStandards: ['Gold Standard', 'VCS'],
      description: 'Seeking comprehensive verification for large-scale reforestation project with community benefits tracking.',
      estimatedCredits: 150000,
      projectOwner: {
        name: 'Green Earth Foundation',
        rating: 4.8,
        projectsCompleted: 12
      },
      proposalsCount: 5,
      proposalsReceived: [
        {
          id: 'PR001',
          verifierId: 'V001',
          verifierName: 'EcoVerify Solutions',
          verifierRating: 4.9,
          verifierExperience: '8 years',
          price: 75000,
          timeline: '4 months',
          methodology: 'Gold Standard CDM methodology with satellite monitoring',
          scope: 'Full project verification including baseline, monitoring, and community impact assessment',
          submittedDate: '2024-01-18',
          status: 'pending',
          credentials: ['Gold Standard', 'VCS', 'CDM'],
          completedProjects: 45
        },
        {
          id: 'PR002',
          verifierId: 'V002',
          verifierName: 'Carbon Trust Verifiers',
          verifierRating: 4.7,
          verifierExperience: '10 years',
          price: 68000,
          timeline: '5 months',
          methodology: 'VCS methodology with ground-based monitoring and drone verification',
          scope: 'Complete verification package with quarterly monitoring reports',
          submittedDate: '2024-01-19',
          status: 'pending',
          credentials: ['VCS', 'CAR', 'ACR'],
          completedProjects: 78
        }
      ]
    },
    {
      id: 'VR002',
      projectId: 'P002',
      projectName: 'Solar Farm Efficiency Project',
      projectType: 'Renewable Energy',
      location: 'California, USA',
      status: 'proposal-accepted',
      requestDate: '2024-01-10',
      deadline: '2024-03-10',
      budgetRange: '25000-50000',
      preferredStandards: ['CAR', 'ACR'],
      description: 'Verification needed for solar farm efficiency improvements and grid integration benefits.',
      estimatedCredits: 80000,
      projectOwner: {
        name: 'SolarTech Industries',
        rating: 4.6,
        projectsCompleted: 8
      },
      proposalsCount: 3,
      acceptedProposal: {
        id: 'PR003',
        verifierId: 'V003',
        verifierName: 'Renewable Verification Corp',
        price: 42000,
        timeline: '3 months'
      }
    },
    {
      id: 'VR003',
      projectId: 'P003',
      projectName: 'Methane Capture Facility',
      projectType: 'Methane Reduction',
      location: 'Texas, USA',
      status: 'in-progress',
      requestDate: '2024-01-05',
      deadline: '2024-02-05',
      budgetRange: '100000-250000',
      preferredStandards: ['CAR', 'VCS'],
      description: 'Complex methane capture verification with continuous monitoring requirements.',
      estimatedCredits: 200000,
      projectOwner: {
        name: 'Energy Solutions LLC',
        rating: 4.9,
        projectsCompleted: 15
      },
      proposalsCount: 7,
      acceptedProposal: {
        id: 'PR004',
        verifierId: 'V001',
        verifierName: 'EcoVerify Solutions',
        price: 180000,
        timeline: '6 months'
      },
      progress: 35
    }
  ]);

  const tabs = userType === 'project-owner' 
    ? [
        { id: 'active-requests', label: 'Active Requests', count: verificationRequests.filter(r => ['pending', 'proposals-received'].includes(r.status)).length },
        { id: 'in-progress', label: 'In Progress', count: verificationRequests.filter(r => r.status === 'in-progress').length },
        { id: 'completed', label: 'Completed', count: verificationRequests.filter(r => r.status === 'completed').length },
        { id: 'create-request', label: 'Create Request', count: null }
      ]
    : [
        { id: 'available-requests', label: 'Available Requests', count: verificationRequests.filter(r => r.status === 'pending').length },
        { id: 'my-proposals', label: 'My Proposals', count: 8 },
        { id: 'active-projects', label: 'Active Projects', count: 3 },
        { id: 'completed-projects', label: 'Completed', count: 12 }
      ];

  const getStatusColor = (status) => {
    const colors = {
      'pending': 'bg-yellow-100 text-yellow-800',
      'proposals-received': 'bg-blue-100 text-blue-800',
      'proposal-accepted': 'bg-green-100 text-green-800',
      'in-progress': 'bg-purple-100 text-purple-800',
      'completed': 'bg-green-100 text-green-800',
      'rejected': 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusLabel = (status) => {
    const labels = {
      'pending': 'Awaiting Proposals',
      'proposals-received': 'Proposals Received',
      'proposal-accepted': 'Proposal Accepted',
      'in-progress': 'Verification In Progress',
      'completed': 'Completed',
      'rejected': 'Rejected'
    };
    return labels[status] || status;
  };

  const filteredRequests = verificationRequests.filter(request => {
    const matchesSearch = request.projectName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         request.location.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         request.projectType.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterStatus === 'all' || request.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  const handleSubmitProposal = async () => {
    if (!selectedRequest) return;
    
    try {
      // Simulate API call
      console.log('Submitting proposal:', {
        requestId: selectedRequest.id,
        verifierId: userId,
        ...proposalData
      });
      
      alert('Proposal submitted successfully!');
      setShowProposalModal(false);
      setProposalData({
        price: '',
        timeline: '',
        methodology: '',
        scope: '',
        deliverables: '',
        experience: '',
        references: ''
      });
    } catch (error) {
      console.error('Error submitting proposal:', error);
      alert('Error submitting proposal. Please try again.');
    }
  };

  const handleAcceptProposal = async (requestId, proposalId) => {
    try {
      console.log('Accepting proposal:', { requestId, proposalId });
      alert('Proposal accepted! Verification process will begin.');
    } catch (error) {
      console.error('Error accepting proposal:', error);
      alert('Error accepting proposal. Please try again.');
    }
  };

  const renderRequestCard = (request) => (
    <div key={request.id} className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <div className="flex items-center space-x-3 mb-2">
            <h3 className="text-lg font-semibold text-gray-900">{request.projectName}</h3>
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(request.status)}`}>
              {getStatusLabel(request.status)}
            </span>
          </div>
          <div className="flex items-center space-x-4 text-sm text-gray-600 mb-3">
            <div className="flex items-center space-x-1">
              <MapPin className="h-4 w-4" />
              <span>{request.location}</span>
            </div>
            <div className="flex items-center space-x-1">
              <Shield className="h-4 w-4" />
              <span>{request.projectType}</span>
            </div>
            <div className="flex items-center space-x-1">
              <Calendar className="h-4 w-4" />
              <span>Due: {new Date(request.deadline).toLocaleDateString()}</span>
            </div>
          </div>
        </div>
        <div className="text-right">
          <div className="text-lg font-semibold text-green-600">
            ${request.budgetRange.split('-')[0]}k - ${request.budgetRange.split('-')[1]}k
          </div>
          <div className="text-sm text-gray-500">
            {request.estimatedCredits.toLocaleString()} tCO2e
          </div>
        </div>
      </div>

      <p className="text-gray-700 mb-4 line-clamp-2">{request.description}</p>

      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-1 text-sm text-gray-600">
            <Users className="h-4 w-4" />
            <span>{request.proposalsCount} proposals</span>
          </div>
          <div className="flex items-center space-x-1 text-sm text-gray-600">
            <Award className="h-4 w-4" />
            <span>{request.preferredStandards.join(', ')}</span>
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setSelectedRequest(request)}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <Eye className="h-4 w-4 inline mr-1" />
            View Details
          </button>
          {userType === 'verifier' && request.status === 'pending' && (
            <button
              onClick={() => {
                setSelectedRequest(request);
                setShowProposalModal(true);
              }}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
            >
              <Send className="h-4 w-4 inline mr-1" />
              Submit Proposal
            </button>
          )}
        </div>
      </div>

      {request.status === 'in-progress' && request.progress && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-600">Verification Progress</span>
            <span className="text-sm font-medium text-gray-900">{request.progress}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-green-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${request.progress}%` }}
            ></div>
          </div>
        </div>
      )}
    </div>
  );

  const renderProposalCard = (proposal) => (
    <div key={proposal.id} className="bg-white rounded-lg border border-gray-200 p-6">
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <div className="flex items-center space-x-3 mb-2">
            <h4 className="text-lg font-semibold text-gray-900">{proposal.verifierName}</h4>
            <div className="flex items-center space-x-1">
              <Star className="h-4 w-4 text-yellow-400 fill-current" />
              <span className="text-sm text-gray-600">{proposal.verifierRating}</span>
            </div>
          </div>
          <div className="flex items-center space-x-4 text-sm text-gray-600 mb-3">
            <span>{proposal.verifierExperience} experience</span>
            <span>{proposal.completedProjects} projects completed</span>
          </div>
        </div>
        <div className="text-right">
          <div className="text-lg font-semibold text-green-600">${proposal.price.toLocaleString()}</div>
          <div className="text-sm text-gray-500">{proposal.timeline}</div>
        </div>
      </div>

      <div className="space-y-3 mb-4">
        <div>
          <h5 className="font-medium text-gray-900 mb-1">Methodology</h5>
          <p className="text-sm text-gray-700">{proposal.methodology}</p>
        </div>
        <div>
          <h5 className="font-medium text-gray-900 mb-1">Scope</h5>
          <p className="text-sm text-gray-700">{proposal.scope}</p>
        </div>
        <div>
          <h5 className="font-medium text-gray-900 mb-1">Credentials</h5>
          <div className="flex flex-wrap gap-1">
            {proposal.credentials.map(credential => (
              <span key={credential} className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                {credential}
              </span>
            ))}
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between pt-4 border-t border-gray-200">
        <div className="text-sm text-gray-500">
          Submitted: {new Date(proposal.submittedDate).toLocaleDateString()}
        </div>
        {userType === 'project-owner' && proposal.status === 'pending' && (
          <div className="flex items-center space-x-2">
            <button
              onClick={() => handleAcceptProposal(selectedRequest.id, proposal.id)}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
            >
              Accept Proposal
            </button>
            <button className="px-4 py-2 text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg hover:bg-gray-50">
              Message Verifier
            </button>
          </div>
        )}
      </div>
    </div>
  );

  const renderCreateRequestForm = () => (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <h3 className="text-xl font-semibold text-gray-900 mb-6">Create Verification Request</h3>
      
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Project</label>
            <select className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent">
              <option value="">Select project</option>
              <option value="P001">Amazon Rainforest Restoration</option>
              <option value="P002">Solar Farm Efficiency Project</option>
              <option value="P003">Wind Energy Development</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Budget Range</label>
            <select className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent">
              <option value="">Select budget range</option>
              <option value="10000-25000">$10,000 - $25,000</option>
              <option value="25000-50000">$25,000 - $50,000</option>
              <option value="50000-100000">$50,000 - $100,000</option>
              <option value="100000-250000">$100,000 - $250,000</option>
              <option value="250000+">$250,000+</option>
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Deadline</label>
            <input
              type="date"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Preferred Timeline</label>
            <select className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent">
              <option value="">Select timeline</option>
              <option value="1-3months">1-3 months</option>
              <option value="3-6months">3-6 months</option>
              <option value="6-12months">6-12 months</option>
              <option value="12months+">12+ months</option>
            </select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Preferred Standards</label>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {['Gold Standard', 'VCS', 'CAR', 'CDM', 'Plan Vivo', 'ACR', 'Climate Forward', 'Social Carbon'].map(standard => (
              <label key={standard} className="flex items-center space-x-2">
                <input type="checkbox" className="rounded border-gray-300 text-green-600 focus:ring-green-500" />
                <span className="text-sm text-gray-700">{standard}</span>
              </label>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Requirements & Scope</label>
          <textarea
            rows={4}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
            placeholder="Describe specific verification requirements, methodologies needed, site visit requirements, etc."
          />
        </div>

        <div className="flex items-center justify-end space-x-4">
          <button className="px-6 py-2 text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg hover:bg-gray-50">
            Save as Draft
          </button>
          <button className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700">
            Submit Request
          </button>
        </div>
      </div>
    </div>
  );

  const renderRequestDetails = () => {
    if (!selectedRequest) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-lg max-w-4xl w-full max-h-screen overflow-y-auto">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">Verification Request Details</h2>
              <button
                onClick={() => setSelectedRequest(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XCircle className="h-6 w-6" />
              </button>
            </div>
          </div>

          <div className="p-6 space-y-6">
            {/* Request Overview */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="font-medium text-gray-900 mb-3">Request Overview</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-medium text-gray-700">Project:</span>
                  <p className="text-gray-900">{selectedRequest.projectName}</p>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Type:</span>
                  <p className="text-gray-900">{selectedRequest.projectType}</p>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Location:</span>
                  <p className="text-gray-900">{selectedRequest.location}</p>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Budget:</span>
                  <p className="text-gray-900">${selectedRequest.budgetRange.replace('-', ' - ')}</p>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Deadline:</span>
                  <p className="text-gray-900">{new Date(selectedRequest.deadline).toLocaleDateString()}</p>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Standards:</span>
                  <p className="text-gray-900">{selectedRequest.preferredStandards.join(', ')}</p>
                </div>
              </div>
            </div>

            {/* Description */}
            <div>
              <h3 className="font-medium text-gray-900 mb-2">Requirements Description</h3>
              <p className="text-gray-700">{selectedRequest.description}</p>
            </div>

            {/* Proposals */}
            {selectedRequest.proposalsReceived && selectedRequest.proposalsReceived.length > 0 && (
              <div>
                <h3 className="font-medium text-gray-900 mb-4">Received Proposals ({selectedRequest.proposalsReceived.length})</h3>
                <div className="space-y-4">
                  {selectedRequest.proposalsReceived.map(proposal => renderProposalCard(proposal))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderProposalModal = () => {
    if (!showProposalModal || !selectedRequest) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-lg max-w-2xl w-full max-h-screen overflow-y-auto">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">Submit Verification Proposal</h2>
              <button
                onClick={() => setShowProposalModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XCircle className="h-6 w-6" />
              </button>
            </div>
          </div>

          <div className="p-6 space-y-6">
            <div className="bg-blue-50 rounded-lg p-4">
              <h3 className="font-medium text-blue-900 mb-2">Project: {selectedRequest.projectName}</h3>
              <p className="text-sm text-blue-700">Budget Range: ${selectedRequest.budgetRange.replace('-', ' - ')}</p>
              <p className="text-sm text-blue-700">Deadline: {new Date(selectedRequest.deadline).toLocaleDateString()}</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Proposed Price ($)</label>
                <input
                  type="number"
                  value={proposalData.price}
                  onChange={(e) => setProposalData(prev => ({ ...prev, price: e.target.value }))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder="75000"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Timeline</label>
                <select
                  value={proposalData.timeline}
                  onChange={(e) => setProposalData(prev => ({ ...prev, timeline: e.target.value }))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                >
                  <option value="">Select timeline</option>
                  <option value="1 month">1 month</option>
                  <option value="2 months">2 months</option>
                  <option value="3 months">3 months</option>
                  <option value="4 months">4 months</option>
                  <option value="5 months">5 months</option>
                  <option value="6 months">6 months</option>
                  <option value="6+ months">6+ months</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Methodology</label>
              <textarea
                value={proposalData.methodology}
                onChange={(e) => setProposalData(prev => ({ ...prev, methodology: e.target.value }))}
                rows={3}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder="Describe your verification methodology and approach"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Scope & Deliverables</label>
              <textarea
                value={proposalData.scope}
                onChange={(e) => setProposalData(prev => ({ ...prev, scope: e.target.value }))}
                rows={3}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder="Detail what will be included in your verification scope"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Relevant Experience</label>
              <textarea
                value={proposalData.experience}
                onChange={(e) => setProposalData(prev => ({ ...prev, experience: e.target.value }))}
                rows={3}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder="Highlight relevant experience with similar projects"
              />
            </div>

            <div className="flex items-center justify-end space-x-4">
              <button
                onClick={() => setShowProposalModal(false)}
                className="px-6 py-2 text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitProposal}
                className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                Submit Proposal
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">
            {userType === 'project-owner' ? 'Verification Requests' : 'Verification Marketplace'}
          </h1>
          <p className="text-gray-600 mt-2">
            {userType === 'project-owner' 
              ? 'Manage your verification requests and review proposals'
              : 'Find verification opportunities and manage your proposals'
            }
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <FileText className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Active Requests</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {userType === 'project-owner' ? '3' : '12'}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">
                  {userType === 'project-owner' ? 'Completed' : 'Won Proposals'}
                </p>
                <p className="text-2xl font-semibold text-gray-900">
                  {userType === 'project-owner' ? '8' : '5'}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Clock className="h-6 w-6 text-yellow-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">In Progress</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {userType === 'project-owner' ? '2' : '3'}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <DollarSign className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">
                  {userType === 'project-owner' ? 'Total Budget' : 'Total Earnings'}
                </p>
                <p className="text-2xl font-semibold text-gray-900">$485k</p>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex items-center space-x-1 mb-6 border-b border-gray-200">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-green-500 text-green-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.label}
              {tab.count !== null && (
                <span className={`ml-2 px-2 py-1 rounded-full text-xs ${
                  activeTab === tab.id
                    ? 'bg-green-100 text-green-800'
                    : 'bg-gray-100 text-gray-600'
                }`}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Content */}
        {activeTab === 'create-request' ? (
          renderCreateRequestForm()
        ) : (
          <>
            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-4 mb-6">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search requests..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                </div>
              </div>
              
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              >
                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                <option value="proposals-received">Proposals Received</option>
                <option value="in-progress">In Progress</option>
                <option value="completed">Completed</option>
              </select>
            </div>

            {/* Request Cards */}
            <div className="space-y-6">
              {filteredRequests.map(request => renderRequestCard(request))}
              
              {filteredRequests.length === 0 && (
                <div className="text-center py-12">
                  <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No requests found</h3>
                  <p className="text-gray-600">
                    {searchTerm || filterStatus !== 'all' 
                      ? 'Try adjusting your search or filters' 
                      : 'No verification requests available at the moment'
                    }
                  </p>
                </div>
              )}
            </div>
          </>
        )}

        {/* Modals */}
        {renderRequestDetails()}
        {renderProposalModal()}
      </div>
    </div>
  );
};

export default VerificationRequestManager; 